import { useId } from 'react';
import { mergePickerItems, type PickerItem } from '@/components/modals/formPickerItems';
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../../components/ui/Dialog';
import { Button } from '../../../../../components/ui/Button';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { InputField } from '../../../../../components/formFields/InputField';
import Bar from '../../../../../components/elements/Bar';
import { Plus } from 'lucide-react';
import PickValueWithClassificationModal from '../../../../../components/modals/PickValueWithClassificationModal';
import Label from '@/components/Label';
import { AreaField } from '../../../../../components/formFields/AreaField';
import FormList from '../../../../../components/elements/FormList';

type TechnicalReportItemModalProps = {
  visible: boolean;
  title: string;
  formProps?: any;
  onSubmit: (id: any, values: any) => void;
  onClose: () => void;
  classification?: any[];
};

const TechnicalReportItemModalForm = ({
  visible,
  title,
  formProps,
  onSubmit,
  onClose,
}: TechnicalReportItemModalProps) => {
  const [visibleAddBiblioDialog, setVisibleAddBiblioDialog] = useState(false);

  const ValidationSchema = Yup.object().shape({
    title: Yup.string().nullable().required('Povinné pole'),
    description: Yup.string().nullable().required('Povinné pole'),
  });

  const pickerSession = useId();
  const formRef = useRef<any>(null);

  const handleDeleteBiblio = (id: any) => {
    if (!formRef?.current) return null;

    const newValues = formRef.current.values.biblios.filter((biblio: any) => biblio.id !== id);

    formRef.current.setFieldValue('biblios', newValues);
  };

  const handleAddBiblio = async (items: PickerItem[]) => {
    await formRef.current.setFieldValue(
      'biblios',
      mergePickerItems(formRef.current.values.biblios, items)
    );
  };

  const onSubmitButton = () => {
    if (!formRef?.current) return null;
    formRef.current.submitForm();
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            validationSchema={ValidationSchema}
            initialValues={{
              title: formProps?.title || null,
              description: formProps?.description || null,
              biblios: formProps?.biblios || [],
            }}
            onSubmit={(values) => {
              onSubmit(formProps?.id, {
                ...values,
                biblios: values.biblios.map((biblio: any) => biblio.id),
              });
            }}
          >
            {(props) => {
              const { values } = props;
              return (
                <>
                  <Form className="space-y-1">
                    <div>
                      <Label name="title" title="Název" required />
                      <Field
                        name="title"
                        value={values.title || null}
                        placeholder="Název"
                        component={InputField}
                        data-testid="tech-report-item-modal-title-input"
                      />
                    </div>

                    <div>
                      <Label name="description" title="Popisek" required />
                      <Field
                        name="description"
                        value={values.description || null}
                        placeholder="Popisek"
                        component={AreaField}
                        data-testid="tech-report-item-modal-description-input"
                      />
                    </div>

                    <div className="mb-1">
                      <Bar
                        text="Biblio"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisibleAddBiblioDialog(true)}
                            data-testid="tech-report-item-modal-add-biblio-btn"
                          />,
                        ]}
                      />
                      <FormList data={values.biblios} onDelete={handleDeleteBiblio} />
                    </div>
                  </Form>
                  <PickValueWithClassificationModal
                    multiple
                    sessionKey={pickerSession}
                    visible={visibleAddBiblioDialog}
                    url="left-panel-biblio"
                    pickerUrl="biblio/left-panel/"
                    onClose={() => setVisibleAddBiblioDialog(false)}
                    onSubmit={handleAddBiblio}
                    alreadySelectedValues={values.biblios}
                  />
                </>
              );
            }}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <Button
            key={1}
            variant="outline"
            onClick={() => onClose()}
            data-testid="tech-report-item-modal-close-btn"
          >
            Zavřít
          </Button>
          <Button
            key={2}
            onClick={() => onSubmitButton()}
            data-testid="tech-report-item-modal-submit-btn"
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function TechnicalReportItemModal(
  props: React.ComponentProps<typeof TechnicalReportItemModalForm>
) {
  return props.visible ? (
    <TechnicalReportItemModalForm
      key={`${props.formProps?.id ?? 'create'}:${props.formProps?.id_version ?? ''}`}
      {...props}
    />
  ) : null;
}

export default TechnicalReportItemModal;
